import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Loader2, Save, Trash2, Package, Grid } from 'lucide-react';
import api from '../../api/client';

/**
 * Category → sub-category → product pricing tree used inside the CRM profile.
 * Rendered as a flat list (not a recursive component) so hot-reload / visual-edit
 * tooling can process the file without hitting a stack overflow.
 *
 * For every product row the admin can set a fixed price (£) and/or a discount %
 * that overrides the default tier pricing for this specific customer.
 * Category rows accept a discount % that applies to all descendant products
 * unless a more specific override is set.
 */
const CategoryCRMPricingTree = ({ customerId, initialOverrides = [], onChanged }) => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [c, p] = await Promise.all([api.get('/categories'), api.get('/products')]);
        if (!mounted) return;
        setCategories(c.data);
        setProducts(p.data);
      } catch (e) {
        toast.error('Failed to load catalogue');
      } finally { setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const d = {};
    for (const o of initialOverrides) {
      const key = `${o.type}::${o.identifier}`;
      d[key] = {
        id: o.id,
        discount_pct: o.discount_pct || 0,
        fixed_price: o.fixed_price != null ? o.fixed_price : '',
        bulk_qty: o.bulk_qty != null ? o.bulk_qty : '',
        bulk_price: o.bulk_price != null ? o.bulk_price : '',
      };
    }
    setDrafts(d);
  }, [initialOverrides]);

  const byParent = useMemo(() => {
    const m = new Map();
    for (const c of categories) {
      const key = c.parent_id || '__root__';
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(c);
    }
    for (const arr of m.values()) arr.sort((a, b) => (a.order || 0) - (b.order || 0) || a.name.localeCompare(b.name));
    return m;
  }, [categories]);

  const productsByCat = useMemo(() => {
    const m = new Map();
    for (const p of products) {
      if (!m.has(p.category)) m.set(p.category, []);
      m.get(p.category).push(p);
    }
    for (const arr of m.values()) arr.sort((a, b) => (a.order || 0) - (b.order || 0) || a.name.localeCompare(b.name));
    return m;
  }, [products]);

  // Flatten to a rendered rows list, iteratively, respecting `expanded`
  const rows = useMemo(() => {
    const out = [];
    const rootCats = byParent.get('__root__') || [];
    const stack = [];
    for (let i = rootCats.length - 1; i >= 0; i--) stack.push({ cat: rootCats[i], depth: 0 });
    while (stack.length) {
      const { cat, depth } = stack.pop();
      out.push({ kind: 'cat', cat, depth });
      if (!expanded[cat.id]) continue;
      const kids = byParent.get(cat.id) || [];
      const prods = productsByCat.get(cat.slug) || [];
      // push products first so they render AFTER child categories at same level
      // Reverse of expected reading order because stack is LIFO
      for (let i = prods.length - 1; i >= 0; i--) out.push({ kind: 'prod', prod: prods[i], depth: depth + 1 });
      for (let i = kids.length - 1; i >= 0; i--) stack.push({ cat: kids[i], depth: depth + 1 });
      if (prods.length === 0 && kids.length === 0) {
        out.push({ kind: 'empty', depth: depth + 1, parentId: cat.id });
      }
    }
    return out;
  }, [byParent, productsByCat, expanded]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const updateDraft = (key, patch) => setDrafts(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

  const saveProductOverride = async (sku) => {
    const key = `product::${sku}`;
    const d = drafts[key] || {};
    const disc = Number(d.discount_pct || 0);
    const fpRaw = d.fixed_price;
    const fp = fpRaw === '' || fpRaw === null || fpRaw === undefined ? null : Number(fpRaw);
    const bqRaw = d.bulk_qty;
    const bq = bqRaw === '' || bqRaw === null || bqRaw === undefined ? null : Number(bqRaw);
    const bpRaw = d.bulk_price;
    const bp = bpRaw === '' || bpRaw === null || bpRaw === undefined ? null : Number(bpRaw);
    if (fp != null && (isNaN(fp) || fp < 0)) { toast.error('Fixed price must be a positive number'); return; }
    if (isNaN(disc) || disc < 0 || disc > 100) { toast.error('Discount must be between 0 and 100'); return; }
    if (bq != null && (isNaN(bq) || bq < 1)) { toast.error('Bulk qty must be a whole number ≥ 1'); return; }
    if (bp != null && (isNaN(bp) || bp < 0)) { toast.error('Bulk price must be a positive number'); return; }
    if ((bq == null) !== (bp == null)) { toast.error('Set both Bulk Qty and Bulk Price, or clear both'); return; }
    setSavingKey(key);
    try {
      await api.post(`/admin/customers/${customerId}/overrides`, {
        type: 'product', identifier: sku,
        discount_pct: disc || 0,
        fixed_price: fp,
        bulk_qty: bq,
        bulk_price: bp,
      });
      toast.success('Product pricing saved');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSavingKey(null); }
  };

  const saveCategoryOverride = async (slug) => {
    const key = `category::${slug}`;
    const d = drafts[key] || {};
    const disc = Number(d.discount_pct || 0);
    if (isNaN(disc) || disc < 0 || disc > 100) { toast.error('Discount must be between 0 and 100'); return; }
    setSavingKey(key);
    try {
      await api.post(`/admin/customers/${customerId}/overrides`, {
        type: 'category', identifier: slug,
        discount_pct: disc || 0,
      });
      toast.success('Category discount saved');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSavingKey(null); }
  };

  const clearOverride = async (type, identifier, overrideId) => {
    const key = `${type}::${identifier}`;
    if (!overrideId) {
      setDrafts(prev => { const n = { ...prev }; delete n[key]; return n; });
      return;
    }
    setSavingKey(key);
    try {
      await api.delete(`/admin/customers/${customerId}/overrides/${overrideId}`);
      setDrafts(prev => { const n = { ...prev }; delete n[key]; return n; });
      toast.success('Removed');
      onChanged?.();
    } catch (e) {
      toast.error('Remove failed');
    } finally { setSavingKey(null); }
  };

  if (loading) {
    return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#F46A25]" /></div>;
  }

  return (
    <div className="border border-neutral-200 rounded-sm divide-y divide-neutral-200" data-testid="crm-pricing-tree">
      {rows.map((r, idx) => {
        if (r.kind === 'empty') {
          return (
            <div key={`empty-${r.parentId}-${idx}`} className="text-xs text-neutral-400 italic py-2 bg-neutral-50/60" style={{ paddingLeft: `${r.depth * 16 + 40}px` }}>
              Empty category
            </div>
          );
        }
        if (r.kind === 'cat') {
          const cat = r.cat;
          const key = `category::${cat.slug}`;
          const draft = drafts[key];
          const kids = byParent.get(cat.id) || [];
          const prods = productsByCat.get(cat.slug) || [];
          const hasContent = kids.length > 0 || prods.length > 0;
          const isOpen = !!expanded[cat.id];
          const catSaving = savingKey === key;
          return (
            <div key={`cat-${cat.id}`} className="flex items-center gap-2 py-2 pr-3 hover:bg-neutral-50" style={{ paddingLeft: `${r.depth * 16 + 8}px` }} data-testid={`crm-cat-${cat.slug}`}>
              <button onClick={() => toggle(cat.id)} className="w-6 h-6 rounded hover:bg-neutral-200 flex items-center justify-center shrink-0" aria-label="Expand">
                {hasContent ? (isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : <span className="w-4 h-4 inline-block" />}
              </button>
              <Grid className="w-3.5 h-3.5 text-[#F46A25] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-[#12263A] truncate">{cat.name}</div>
                <div className="text-[10px] text-neutral-500 font-mono truncate">{cat.slug}</div>
              </div>
              <div className="flex items-center gap-1">
                <label className="text-[10px] uppercase font-bold text-neutral-500">Cat Disc %</label>
                <input
                  type="number" min="0" max="100" step="0.1"
                  value={draft?.discount_pct ?? ''}
                  onChange={e => updateDraft(key, { discount_pct: e.target.value })}
                  data-testid={`crm-cat-discount-${cat.slug}`}
                  className="w-20 border border-neutral-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-[#F46A25]"
                />
                <button
                  onClick={() => saveCategoryOverride(cat.slug)}
                  disabled={catSaving}
                  data-testid={`crm-cat-save-${cat.slug}`}
                  className="text-[11px] bg-[#12263A] hover:bg-black text-white font-bold px-2 py-1 rounded-sm flex items-center gap-1 disabled:opacity-60"
                >
                  {catSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                </button>
                {draft?.id && (
                  <button
                    onClick={() => clearOverride('category', cat.slug, draft.id)}
                    className="text-red-600 hover:bg-red-50 p-1 rounded"
                    aria-label="Remove category override"
                  ><Trash2 className="w-3 h-3" /></button>
                )}
              </div>
            </div>
          );
        }
        // product row
        const p = r.prod;
        const key = `product::${p.sku}`;
        const draft = drafts[key];
        const prodSaving = savingKey === key;
        return (
          <div key={`prod-${p.id}`} className="flex items-center gap-2 py-2 pr-3 hover:bg-white bg-neutral-50/60" style={{ paddingLeft: `${r.depth * 16 + 40}px` }} data-testid={`crm-prod-${p.sku}`}>
            <Package className="w-3.5 h-3.5 text-neutral-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#12263A] truncate">{p.name}</div>
              <div className="flex items-center gap-2 text-[10px] text-neutral-500 font-mono flex-wrap">
                <span>{p.sku}</span>
                <span>·</span>
                <span>£{Number(p.price).toFixed(2)} base</span>
                {p.bulk_qty && p.bulk_price != null && (
                  <>
                    <span>·</span>
                    <span title="Standard bulk pricing (falls back for this user if no bulk override is set)">
                      Std bulk: ≥{p.bulk_qty} @ £{Number(p.bulk_price).toFixed(2)}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-wrap justify-end">
              <label className="text-[10px] uppercase font-bold text-neutral-500">£ Fixed</label>
              <input
                type="number" min="0" step="0.01"
                value={draft?.fixed_price ?? ''}
                onChange={e => updateDraft(key, { fixed_price: e.target.value })}
                placeholder="—"
                data-testid={`crm-prod-fixed-${p.sku}`}
                className="w-20 border border-neutral-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-[#F46A25]"
              />
              <label className="text-[10px] uppercase font-bold text-neutral-500 ml-1">Disc %</label>
              <input
                type="number" min="0" max="100" step="0.1"
                value={draft?.discount_pct ?? ''}
                onChange={e => updateDraft(key, { discount_pct: e.target.value })}
                placeholder="—"
                data-testid={`crm-prod-discount-${p.sku}`}
                className="w-16 border border-neutral-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-[#F46A25]"
              />
              <label className="text-[10px] uppercase font-bold text-neutral-500 ml-1" title="Custom bulk trigger qty for this customer. Leave blank to fall back to the product's standard bulk qty.">Bulk Qty</label>
              <input
                type="number" min="1" step="1"
                value={draft?.bulk_qty ?? ''}
                onChange={e => updateDraft(key, { bulk_qty: e.target.value })}
                placeholder="—"
                data-testid={`crm-prod-bulk-qty-${p.sku}`}
                className="w-16 border border-neutral-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-[#F46A25]"
              />
              <label className="text-[10px] uppercase font-bold text-neutral-500 ml-1" title="Custom bulk unit price for this customer when qty ≥ Bulk Qty.">£ Bulk</label>
              <input
                type="number" min="0" step="0.01"
                value={draft?.bulk_price ?? ''}
                onChange={e => updateDraft(key, { bulk_price: e.target.value })}
                placeholder="—"
                data-testid={`crm-prod-bulk-price-${p.sku}`}
                className="w-20 border border-neutral-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-[#F46A25]"
              />
              <button
                onClick={() => saveProductOverride(p.sku)}
                disabled={prodSaving}
                data-testid={`crm-prod-save-${p.sku}`}
                className="text-[11px] bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold px-2 py-1 rounded-sm flex items-center gap-1 disabled:opacity-60"
              >
                {prodSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
              </button>
              {draft?.id && (
                <button
                  onClick={() => clearOverride('product', p.sku, draft.id)}
                  className="text-red-600 hover:bg-red-50 p-1 rounded"
                  aria-label="Remove product override"
                  data-testid={`crm-prod-clear-${p.sku}`}
                ><Trash2 className="w-3 h-3" /></button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CategoryCRMPricingTree;
